import { type ErrorInfo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { type SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';

/**
 * Creates a shared error handler for unhandled errors in AI Mate components.
 * This function sends analytics events when errors occur in ErrorBoundary components.
 *
 * @param sendAnalyticsEvent - The analytics event sender function
 * @param sourceComponent - The name of the component where the error occurred
 * @returns A callback function that can be used with ErrorBoundary onError prop
 */
export const createUnhandledErrorHandler = (
	sendAnalyticsEvent: SendAnalyticsEvent,
	sourceComponent: string,
) => {
	return (error: Error, info: ErrorInfo): void => {
		if (!fg('ai_mate_unhandled_error_reporting')) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'operational',
			action: 'unhandledError',
			actionSubject: 'aiMate',
			attributes: {
				errorName: error?.name,
				errorMessage: error?.message,
				errorStack: error?.stack ?? undefined,
				componentStack: info?.componentStack ?? undefined,
				componentName: info?.componentStack
					? info.componentStack
							.split('\n')
							.find(Boolean)
							?.trim()
							?.replace(/^at\s+/, '')
					: undefined,
				sourceComponent,
			},
		});
	};
};
